'use client'

import { useState, useEffect } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '../../components/ui/button'
import type { ExperimentWithStats } from './types'
import { ABTestsTable } from './components/AbTestsTable'
import { TestDetailView } from './components/TestDetailView'
import { CreateTestModal } from './components/CreateTestModal'
import { TableLoadingSkeleton, DetailLoadingSkeleton } from './components/TableLoadingSkeleton'
import { getExperimentsForUser, getExperimentsListBulk, putUpdateExperiment } from '../../services/experiments/experimentsService'
import { Experiment } from './types'
import { ExperimentStats } from './types'
import { decodeTokenService } from '../../services/decodeTokenService'
import toast from 'react-hot-toast'
import { dictionary } from '@/utils/dictionary'
import { useAuth } from '@/context/AuthContext'

export default function AbTestingPage({ onRegisterCreate }: { onRegisterCreate?: (fn: () => void) => void } = {}) {
  const [experiments, setExperiments] = useState<ExperimentWithStats[]>([])
  const [viewMode, setViewMode] = useState<'list' | 'detail'>('list')
  const [selectedExperiment, setSelectedExperiment] = useState<ExperimentWithStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDetailLoading, setIsDetailLoading] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    if (onRegisterCreate) {
      onRegisterCreate(() => setIsModalOpen(true));
    }
  }, [onRegisterCreate]);
  const { language } = useAuth()

  useEffect(() => {
    async function loadData() {
      try {
        const owner_id = decodeTokenService.getUserIdFromToken()

        const experimentsData: Experiment[] = await getExperimentsForUser()

        if (!experimentsData.length) {
          setExperiments([])
          return
        }

        const experimentsByWebsite: Record<string, Experiment[]> =
          experimentsData.reduce((acc, exp) => {
            if (!acc[exp.website_id]) {
              acc[exp.website_id] = []
            }
            acc[exp.website_id].push(exp)
            return acc
          }, {} as Record<string, Experiment[]>)

        const bulkPayload = Object.entries(experimentsByWebsite).map(
          ([website_id, experiments]) => ({
            owner_id: owner_id || '',
            website_id,
            experiment_ids: experiments.map(exp => exp.id)
          })
        )

        const statsResponses = await getExperimentsListBulk(bulkPayload)

        const allStats: ExperimentStats[] = statsResponses.flatMap(
          (item: any) => item.experiments
        )

        const experimentsWithStats: ExperimentWithStats[] =
          experimentsData.map(exp => {
            const stats = allStats.find(
              stat => stat.experiment_id === exp.id
            )

            return {
              ...exp,
              stats
            }
          })

        setExperiments(experimentsWithStats)
      } catch (error) {
        console.error(error)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [])



  const handleViewExperiment = (experiment: ExperimentWithStats) => {
    setIsDetailLoading(true)
    setSelectedExperiment(experiment)
    setViewMode('detail')

    setTimeout(() => {
      setIsDetailLoading(false)
    }, 600)
  }

  const handleBackToList = () => {
    setViewMode('list')
    setSelectedExperiment(null)
  }

  const handleTogglePause = (experimentId: string) => {
    putUpdateExperiment(experimentId.toString(), { status: experiments.find(exp => exp.id.toString() === experimentId)?.status === 'active' ? 'paused' : 'active' })
      .then(() => {
        toast.success(dictionary['test-updated-successfully'][language])
        window.location.reload()
      })
      .catch(() => {
        toast.error(dictionary['error-updating-test'][language])
      })
  }

  return (
    <div>
      <div className="w-full">

        {viewMode === 'list' ? (
          isLoading ? (
            <TableLoadingSkeleton />
          ) : (
            <ABTestsTable
              experiments={experiments}
              onView={handleViewExperiment}
              onTogglePause={handleTogglePause}
            />
          )
        ) : (
          isDetailLoading ? (
            <DetailLoadingSkeleton />
          ) : selectedExperiment ? (
            <TestDetailView
              selectedExperiment={selectedExperiment}
              onBack={handleBackToList}
            />
          ) : null
        )}

        <CreateTestModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      </div>
    </div>
  )
}
